"use client";

import Link from "next/link";

import { RecipeThumb } from "@/components/shared/RecipeThumb";
import { cn } from "@/lib/utils";

interface RecipeLinkRowProps {
  title: string;
  image: string | null;
  /** The recipe's page. Without it the row is plain text: a recipe that is no longer on the calendar. */
  href?: string;
  /** A line under the title, such as the shopping-list items this recipe needs. */
  detail?: string;
  size?: "md" | "sm";
}

/**
 * One recipe as a row: its photo, its title and an optional line under it. The whole row is the link, so there is no
 * underline or chevron; a soft tint shows on hover. The row's padding is matched by a negative margin, so the photo
 * lines up with the text above and below it.
 */
export function RecipeLinkRow({ title, image, href, detail, size = "md" }: RecipeLinkRowProps) {
  const content = (
    <>
      <RecipeThumb image={image} size={size} />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-sm",
            href ? "font-medium text-foreground" : "text-muted-foreground"
          )}
        >
          {title}
        </span>
        {detail && <span className="line-clamp-2 text-xs text-muted-foreground">{detail}</span>}
      </span>
    </>
  );

  const rowClass = "-mx-2 flex items-center gap-3 rounded-lg px-2 py-1";

  if (!href) return <div className={rowClass}>{content}</div>;

  return (
    <Link
      href={href}
      className={cn(
        rowClass,
        "cursor-pointer transition-colors duration-150 hover:bg-secondary-dark/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      )}
    >
      {content}
    </Link>
  );
}
